import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { snackbar } from '@occc-app/design-system';
import { LinkItem } from '@occc/ui-design-system';
import { useCreateKioskAdSetMutation } from '@occc/graphql';

import { AdSetsCreatePage } from './ad-sets-create-page.component';

import { AdSetFormValues } from '../../touchpoint.types';
import { routeCreator } from '../../touchpoint.router';
import {
  AD_SET_CREATE_SUCCESS_MESSAGE,
  AD_SET_CREATE_ERROR_MESSAGE,
} from '../../touchpoint.constants';
import { isScheduleValid, mapScheduleToScheduleInput } from '../../utils/ad-sets-form.validation';

export const AdSetsCreatePageContainer = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [createKioskAdSet] = useCreateKioskAdSetMutation();

  const breadcrumbLinks: LinkItem[] = useMemo(
    () => [
      {
        to: routeCreator.adSetsList(),
        label: 'Touchpoint',
      },
      {
        key: routeCreator.adSetsCreate(),
        label: 'Ad Sets',
      },
    ],
    [],
  );

  const handleCancel = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const onSubmit = useCallback(
    async (values: AdSetFormValues) => {
      setIsSubmitting(true);

      const schedules = (values.schedules ?? [])
        .filter(isScheduleValid)
        .map(mapScheduleToScheduleInput);

      try {
        const { data } = await createKioskAdSet({
          variables: {
            input: {
              name: values.name.trim(),
              description: values.description.trim(),
              assetId: values.assetId,
              offerId: values.offer?.id ?? null,
              kioskIds: values.kioskIds || [],
              schedules,
            },
          },
          refetchQueries: ['kioskAdSets'],
        });

        if (data?.createKioskAdSet) {
          snackbar.success(AD_SET_CREATE_SUCCESS_MESSAGE);
          navigate(routeCreator.adSetsList());
        } else {
          snackbar.error(AD_SET_CREATE_ERROR_MESSAGE);
          setIsSubmitting(false);
        }
      } catch (error) {
        console.error('Error creating ad set:', error);
        snackbar.error(AD_SET_CREATE_ERROR_MESSAGE);
        setIsSubmitting(false);
      }
    },
    [createKioskAdSet, navigate],
  );

  return (
    <AdSetsCreatePage
      breadcrumbLinks={breadcrumbLinks}
      onSubmit={onSubmit}
      onCancel={handleCancel}
      isSubmitting={isSubmitting}
    />
  );
};
